import { z } from "zod";

/**
 * Relation row identifier — 26-char Crockford-base32 ULID. Width matches
 * the entity_relations.id column. Forward-compatible if the ID format
 * broadens beyond ULID — column width and schema max change together.
 */
export const RelationIdSchema = z.string().length(26).brand("RelationId");
export type RelationId = z.infer<typeof RelationIdSchema>;

/**
 * Relation-kind identifier. Branded string; max 128 chars to match the
 * entity_relations.kind column.
 */
export const RelationKindIdSchema = z
  .string()
  .min(1)
  .max(128)
  .brand("RelationKindId");
export type RelationKindId = z.infer<typeof RelationKindIdSchema>;

/**
 * Entity identifier. Branded string; max 255 chars to match
 * entity_instances.id and the endpoint columns.
 */
export const EntityIdSchema = z.string().min(1).max(255).brand("EntityId");
export type EntityId = z.infer<typeof EntityIdSchema>;

export type Relation<TAttrs = unknown> = {
  readonly id: RelationId;
  readonly kind: RelationKindId;
  readonly endpointA: EntityId;
  readonly endpointB: EntityId;
  readonly attrs: TAttrs;
  readonly createdAt: Date;
};

/**
 * Symbol used in op payloads to defer entity-ID resolution to the apply
 * phase. The `$` field is the runtime discriminator (not a Zod brand or
 * Symbol-tagged type).
 */
export type PlaceholderRef = { readonly $: string };

export const PlaceholderRefSchema = z.object({ $: z.string().min(1) }).strict();

export const isPlaceholderRef = (v: unknown): v is PlaceholderRef =>
  typeof v === "object" &&
  v !== null &&
  "$" in v &&
  typeof (v as { $: unknown }).$ === "string";

/**
 * Patch object for relation update ops. Absent / undefined leaves the
 * field untouched; explicit null clears (must be allowed by attrsSchema);
 * concrete value replaces.
 */
export type PartialWithNull<T> = {
  [K in keyof T]?: T[K] | null;
};

/**
 * Write currency for the relations primitive. Discriminated by `op`.
 */
export type RelationOp =
  | {
      readonly op: "create";
      readonly kind: RelationKindId;
      readonly endpointA: EntityId | PlaceholderRef;
      readonly endpointB: EntityId | PlaceholderRef;
      readonly attrs: unknown; // validated against the kind's attrsSchema
    }
  | {
      readonly op: "update";
      readonly id: RelationId;
      readonly patch: PartialWithNull<unknown>; // shallow-merged then full-validated
    }
  | {
      readonly op: "delete";
      readonly id: RelationId;
      // LAF-94b §9.3 Part A — optional intent annotation propagated through
      // the op-pipeline into RelationOpAppliedPayload.intent. Open string
      // type (not a literal union) so future intents can ride the same
      // channel without substrate amendments.
      readonly reason?: string;
    };
